import React from 'react';
import { Card, CardHeader, EmptyState, Spinner, Tooltip } from '@opendate-app/setlist';
import { SERIES, formatCount } from './bookingUtils';

const LAST_YEAR_COLOR = 'var(--sl-fg-quaternary)';
const LEGEND = [
  { label: 'Confirmed', color: SERIES.confirms.color },
  { label: 'Held', color: SERIES.holds.color },
  { label: 'Open', color: 'var(--sl-bg-tertiary)' },
  { label: 'Confirmed at this point LY', color: LAST_YEAR_COLOR },
];

const share = (part, total) => (total ? (part / total) * 100 : 0);

export function paceVsLastYear(confirmed, lastYearConfirmed) {
  const delta = confirmed - lastYearConfirmed;
  if (delta === 0) return { text: 'Even with LY', color: 'var(--sl-text-tertiary)' };
  const count = Math.abs(delta);
  return {
    text: `${delta > 0 ? '↑' : '↓'} ${formatCount(count)} ${count === 1 ? 'event' : 'events'} vs LY`,
    color: delta > 0 ? 'var(--sl-text-success)' : 'var(--sl-text-error)',
  };
}

const formatAsOf = (iso) => new Date(`${iso}T00:00:00`)
  .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export function lastYearPaceTooltip(row) {
  return [
    `${row.last_year_month} had ${formatCount(row.last_year_confirmed)} confirmed as of ${formatAsOf(row.last_year_as_of)}.`,
    `${row.month} has ${formatCount(row.confirmed)} confirmed as of ${formatAsOf(row.as_of)}.`,
  ];
}

function LegendDot({ color, label }) {
  return (
    <span className="d-inline-flex align-items-center" style={{ gap: 6, fontSize: 12, color: 'var(--sl-text-tertiary)' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
      {label}
    </span>
  );
}

function ForwardMonth({ row }) {
  const confirmedPct = share(row.confirmed, row.capacity);
  const heldPct = share(row.held, row.capacity);
  const pace = paceVsLastYear(row.confirmed, row.last_year_confirmed);
  const [lastYear, thisYear] = lastYearPaceTooltip(row);
  return (
    <div>
      <div className="d-flex justify-content-between flex-wrap" style={{ fontSize: 13, marginBottom: 6, gap: 8 }}>
        <span style={{ fontWeight: 600, color: 'var(--sl-text-secondary)' }}>{row.month}</span>
        <span style={{ color: 'var(--sl-text-tertiary)' }}>
          <span style={{ fontWeight: 600, color: 'var(--sl-text-secondary)' }}>{Math.round(confirmedPct)}%</span>
          {' confirmed · '}
          <Tooltip title={<>{lastYear}<br />{thisYear}</>}>
            <span tabIndex={0} style={{ fontWeight: 600, color: pace.color, cursor: 'help' }}>{pace.text}</span>
          </Tooltip>
          {` · ${formatCount(row.open)} open`}
        </span>
      </div>
      <div
        role="img"
        aria-label={`${row.month}: ${row.confirmed} confirmed, ${row.held} held, ${row.open} open of ${row.capacity} room-nights`}
        style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden', background: 'var(--sl-bg-tertiary)' }}
      >
        <div style={{ width: `${confirmedPct}%`, background: SERIES.confirms.color }} />
        <div style={{ width: `${heldPct}%`, background: SERIES.holds.color }} />
      </div>
      <div
        role="img"
        aria-label={`${row.month} last year at this point: ${row.last_year_confirmed} confirmed`}
        style={{ height: 4, marginTop: 3, borderRadius: 2, overflow: 'hidden' }}
      >
        <div style={{ width: `${row.last_year_pct}%`, height: '100%', background: LAST_YEAR_COLOR }} />
      </div>
    </div>
  );
}

function BookingForwardCalendarCard({ months, loading, error }) {
  let body;
  if (loading) {
    body = <div className="d-flex justify-content-center py-5"><Spinner label="Loading…" /></div>;
  } else if (error) {
    body = <EmptyState title={error} />;
  } else if (!months?.length) {
    body = <EmptyState title="No venues to plan" />;
  } else {
    body = (
      <>
        <div className="d-flex flex-wrap mb-3" style={{ gap: 12 }}>
          {LEGEND.map((item) => <LegendDot key={item.label} {...item} />)}
        </div>
        <div className="d-flex flex-column" style={{ gap: 16 }}>
          {months.map((row) => <ForwardMonth key={row.month} row={row} />)}
        </div>
      </>
    );
  }

  return (
    <Card
      header={(
        <CardHeader
          title="Forward calendar"
          supportingText="Room-nights across your venues, from today through the next 5 months. Today's holds are next quarter's confirms."
        />
      )}
    >
      {body}
    </Card>
  );
}

export default BookingForwardCalendarCard;
